import { useCallback, useMemo } from 'react';

import { Permission } from '@/entities/permissions';
import { useCsvDownloadContext } from '@/features/_shared/chart/context/CsvTableContext';
import ExportButton from '@/features/_shared/chart/ExportButton';
import { usePermission } from '@/features/_shared/user';
import { ExportSelectionSource } from '@/features/data-hub/hooks/useOpenExportSelection';
import type { CsvWidgetExportSource } from '@/features/data-hub/types/context.types';
import { useAppDispatch } from '@/store/hooks';
import { openExportDrawer } from '@/store/slices/drawer.slice';
import { setExport } from '@/store/slices/export.slice';

export const CsvWidgetExportButton = ({ tabName }: { tabName?: string }) => {
  const { hasPermission } = usePermission();
  const { downloadFileId, selectedRange, fileName, groupName, dateColumnKey } =
    useCsvDownloadContext();
  const dispatch = useAppDispatch();
  const isPermissionGranted = hasPermission(Permission.ExportBasic);
  const source = useMemo<CsvWidgetExportSource | null>(() => {
    if (downloadFileId) {
      return { sourceType: 'query', queryFileId: downloadFileId, selectedRange };
    }
    if (fileName && groupName) {
      return { sourceType: 'datalake', fileName, groupName };
    }
    return null;
  }, [downloadFileId, selectedRange, fileName, groupName]);
  const isExportSourceAvailable = source !== null;
  const isButtonDisabled = !isPermissionGranted || !isExportSourceAvailable;

  const handleClick = useCallback(() => {
    if (!source) return;

    dispatch(
      setExport({
        selectedTimeRange:
          source.sourceType === 'query' ? (source.selectedRange ?? undefined) : undefined
      })
    );
    dispatch(
      openExportDrawer({
        showHistory: false,
        source: ExportSelectionSource.WIDGET_BUTTON,
        csvWidgetExportContext: { source, dateColumnKey: dateColumnKey ?? null, tabName }
      })
    );
  }, [dateColumnKey, dispatch, source, tabName]);

  return (
    <ExportButton
      disabled={isButtonDisabled}
      handleClick={handleClick}
      title='Download as CSV'
    />
  );
};
